class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  // see decisions.md: size filter is in-stock only
  static withSizeAvailability(searchParams) {
    const sizeParam = document.getElementById('FacetFiltersFormMobile')?.dataset.sizeFilterParam;
    if (!sizeParam) return searchParams;

    const params = new URLSearchParams(searchParams);
    const current = params.getAll('filter.v.availability').join();
    const wanted = params.has(sizeParam) ? '1' : '';
    if (current === wanted) return searchParams;

    params.delete('filter.v.availability');
    if (wanted) params.append('filter.v.availability', wanted);
    return params.toString();
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    searchParams = FacetFiltersForm.withSizeAvailability(searchParams);
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
  }

  static renderProductGridContainer(html) {
    const container = document.getElementById('ProductGridContainer');

    // Measured before the swap: a detached node reports a zeroed rect.
    const view = container.closest('collection-view');
    const revealsRender = typeof view?.captureCardPositions === 'function';

    if (revealsRender) view.captureCardPositions();

    container.innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    if (revealsRender) return;

    container.querySelectorAll('.scroll-trigger').forEach((element) => {
      element.classList.add('scroll-trigger--cancel');
    });
  }

  static renderProductCount(html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const count = parsedHTML.getElementById('ProductCount').innerHTML;

    // The search heading sits outside every re-rendered region.
    const searchHeading = document.getElementById('SearchResultsHeading');
    const searchHeadingSource = parsedHTML.getElementById('SearchResultsHeading');
    if (searchHeading && searchHeadingSource) searchHeading.innerHTML = searchHeadingSource.innerHTML;

    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    // Snapshot open accordions; syncFacetDetailsState() force-closes them below.
    const openMobileFacetIds = Array.from(
      document.querySelectorAll('#FacetFiltersFormMobile .mobile-facets__details[open]')
    ).map((details) => details.id);
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
    FacetFiltersForm.syncFacetDetailsState();

    // Reopen what was open; only a filter/sort passes an event, Reset All leaves them closed.
    if (event) {
      openMobileFacetIds.forEach((id) => {
        const details = document.getElementById(id);
        if (details) details.open = true;
      });
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting', '.mobile-facets__apply-count'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
    FacetFiltersForm.updateAppliedFilterCount();
  }

  static updateAppliedFilterCount() {
    const form = document.getElementById('FacetFiltersFormMobile');

    if (!form) return;

    let totalCount = 0;

    /*
     * ----------------------------------------
     * COUNT EACH FILTER SECTION
     * ----------------------------------------
     */
    const filterSections = form.querySelectorAll(
      '.mobile-facets__details'
    );

    filterSections.forEach((section) => {
      const sectionCountElement = section.querySelector(
        '[data-facet-section-count]'
      );

      if (!sectionCountElement) return;

      const checkedInputs = section.querySelectorAll(
        'input[type="checkbox"]:checked'
      );

      let sectionCount = checkedInputs.length;

      // Price counts once if either min or max is set.
      const priceInputs = section.querySelectorAll(
        'input[type="number"], input[type="range"]'
      );

      if (priceInputs.length) {
        const hasPriceValue = Array.from(priceInputs).some((input) => {
          return input.value !== '';
        });

        if (hasPriceValue) {
          sectionCount += 1;
        }
      }

      if (sectionCount > 0) {
        sectionCountElement.textContent = `(${sectionCount})`;
        sectionCountElement.hidden = false;
      } else {
        sectionCountElement.textContent = '';
        sectionCountElement.hidden = true;
      }

      totalCount += sectionCount;
    });

    /*
     * ----------------------------------------
     * MAIN FILTER TRIGGER COUNT
     * ----------------------------------------
     */
    const triggerCount = document.querySelector(
      '.mobile-facets__applied-count'
    );

    if (triggerCount) {
      triggerCount.textContent = totalCount;
      triggerCount.hidden = totalCount === 0;
    }

    /*
     * ----------------------------------------
     * RESET ALL TOTAL COUNT
     * ----------------------------------------
     */
    const resetCount = form.querySelector(
      '[data-facet-reset-count]'
    );

    if (resetCount) {
      if (totalCount > 0) {
        resetCount.textContent = `(${totalCount})`;
        resetCount.hidden = false;
      } else {
        resetCount.textContent = '';
        resetCount.hidden = true;
      }
    }
  }

  static syncFacetDetailsState() {
  const isDesktop = window.matchMedia('(min-width: 769px)').matches;
  document
    .querySelectorAll('#FacetFiltersForm details.js-filter, #FacetFiltersForm .js-filter')
    .forEach((details) => {
      if (!(details instanceof HTMLDetailsElement)) return;

      if (isDesktop) {
        details.open = true;
      }
    });

  document
    .querySelectorAll('#FacetFiltersFormMobile .mobile-facets__details')
    .forEach((details) => {
      if (!isDesktop) {
        return;
      }

      details.open = true;
    });

  // see known-issues.md: desktop filter summaries are headings
  document.querySelectorAll('#FacetFiltersFormMobile .mobile-facets__summary').forEach((summary) => {
    if (isDesktop) {
      summary.setAttribute('role', 'heading');
      summary.setAttribute('aria-level', '3');
      summary.setAttribute('tabindex', '-1');
    } else {
      summary.removeAttribute('role');
      summary.removeAttribute('aria-level');
      summary.removeAttribute('tabindex');
    }
  });
}

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    // Merge every filter/sort form so one never drops the other's state.
    const formIdsToMerge = [
      'FacetFiltersFormMobile',
      'FacetFiltersForm',
      'FacetSortForm',
      'FacetSortDrawerForm',
      'FacetFiltersPillsForm',
    ];
    const forms = [];
    document.querySelectorAll('facet-filters-form form').forEach((form) => {
      if (formIdsToMerge.includes(form.id)) {
        forms.push(this.createSearchParams(form));
      }
    });
    this.onSubmitForm(forms.join('&'), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

// Links from outside the drawer (menu, shared URL) can arrive with size and availability out of step.
(() => {
  const searchParams = FacetFiltersForm.searchParamsInitial;
  const normalized = FacetFiltersForm.withSizeAvailability(searchParams);
  if (normalized === searchParams) return;

  FacetFiltersForm.searchParamsInitial = normalized;
  history.replaceState({ searchParams: normalized }, '', `${window.location.pathname}${normalized && '?'.concat(normalized)}`);
  FacetFiltersForm.renderPage(normalized, null, false);
})();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);

document.addEventListener('DOMContentLoaded', () => {
  FacetFiltersForm.updateAppliedFilterCount();
});

document.addEventListener('DOMContentLoaded', () => {
  FacetFiltersForm.updateAppliedFilterCount();
  FacetFiltersForm.syncFacetDetailsState();
});

window
  .matchMedia('(min-width: 768px)')
  .addEventListener('change', () => {
    FacetFiltersForm.syncFacetDetailsState();
});

document.addEventListener('click', (event) => {
  const overlay = event.target.closest('.mobile-facets__overlay');

  if (!overlay) return;

  const details = overlay.closest('.mobile-facets__disclosure');

  if (!details) return;

  // Let MenuDrawer close it so the slide-out runs; removing [open] here hides it instantly.
  const drawer = overlay.closest('menu-drawer');

  if (drawer && typeof drawer.closeMenuDrawer === 'function') {
    drawer.closeMenuDrawer(event, drawer.querySelector('summary'));
    setTimeout(() => {
    FacetFiltersForm.syncFacetDetailsState();
  }, 450);
    return;
  }

  // Fallback for a not-yet-upgraded custom element: instant, but still closed.
  details.removeAttribute('open');
  details.classList.remove('menu-opening');
});

// pointer-events: none does not stop a screen reader's activate click.
document.addEventListener('click', (event) => {
  const summary = event.target.closest('#FacetFiltersFormMobile .mobile-facets__summary');

  if (summary && window.matchMedia('(min-width: 769px)').matches) event.preventDefault();
});

document.addEventListener('click', (event) => {
  document
    .querySelectorAll('details.sorting.disclosure-dropdown[open]')
    .forEach((details) => {
      if (details.contains(event.target)) return;

      details.removeAttribute('open');
    });
});

document.addEventListener(
  'toggle',
  (event) => {
    const details = event.target;

    if (!(details instanceof HTMLDetailsElement)) return;

    const isDesktop = window.matchMedia('(min-width: 769px)').matches;

    if (!isDesktop) return;
    if (!details.matches('#FacetFiltersForm details.js-filter, #FacetFiltersForm .js-filter')) {
      return;
    }
    if (!details.open) {
      details.open = true;
    }
  },
  true
);